import { formatObservationValue, type WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTime } from '../../utils/date';
import { extractReferenceRange } from '../../utils/observation';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Item, ItemContent } from '../ui/item';
import { LabTrendChart } from './LabTrendChart';
import { formatInterpretation, isCriticalObservation } from './observationDisplayUtils';
import { type PromotionStatus, RecordPromotionButton } from './RecordPromotionButton';

export type LabDetailProps = {
  labName: string;
  results: WithId<Observation>[];
  onBack?: () => void;
  /** What `onBack` returns to, when it is not the lab results list. */
  backLabel?: string;
  /**
   * Map of result Observation id → the lab order (`ServiceRequest`) it came from, when one exists.
   * Manually-entered results and results with no linked order are simply absent from the map.
   * The orchestrator resolves these (a lab result has no direct back-reference to its order — see
   * `docs/design-decisions/2026-06-22-modeling-lab-orders-and-results.md`).
   */
  resultOrderLinks?: Record<string, { serviceRequestId: string; display?: string }>;
  /** Invoked with the `ServiceRequest` id when a result's lab-order link is clicked. */
  onSelectLabOrder?: (serviceRequestId: string) => void;
  /** Copy the latest reading into the chart as a Liza-authored result. Offered only for an imported
   * lab — a result Liza already owns has nothing to promote. */
  onPromote?: () => void;
  promotionStatus?: PromotionStatus;
};

export const LabDetail = ({
  labName,
  results,
  onBack,
  backLabel,
  resultOrderLinks,
  onSelectLabOrder,
  onPromote,
  promotionStatus,
}: LabDetailProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const sortedResults = [...results].sort(
    (a, b) => new Date(b.effectiveDateTime || '').getTime() - new Date(a.effectiveDateTime || '').getTime(),
  );
  const latest = sortedResults[0];
  const resultCount = results.length;

  const renderInterpretation = (observation: WithId<Observation>): React.ReactElement | null => {
    const text = formatInterpretation(observation.interpretation);
    if (!text) return null;
    return (
      <Badge variant={isCriticalObservation(observation) ? 'destructive' : 'secondary'} className="text-xs">
        {text}
      </Badge>
    );
  };

  const renderNotes = (observation: WithId<Observation>): React.ReactElement | null => {
    const text = (observation.note ?? [])
      .map((n) => n.text)
      .filter((note): note is string => Boolean(note))
      .join('\n');
    if (!text) return null;
    return (
      <div className="text-xs">
        <span className="text-muted-foreground">{t('notes')}</span> <span className="whitespace-pre-wrap">{text}</span>
      </div>
    );
  };

  const renderLabOrderLink = (observation: WithId<Observation>): React.ReactElement | null => {
    const link = resultOrderLinks?.[observation.id];
    if (!link || !onSelectLabOrder) return null;
    return (
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 text-xs"
        onClick={() => onSelectLabOrder(link.serviceRequestId)}
        data-testid="lab-result-order-link"
      >
        {t('viewLabOrder')}
      </Button>
    );
  };

  if (!latest) {
    return (
      <div className="mx-auto w-full max-w-4xl p-5" data-testid="lab-detail">
        {onBack && (
          <Button
            id="back-to-lab-results-button"
            variant="ghost"
            onClick={onBack}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeftIcon className="size-4" />
            <span>{backLabel ?? t('backToLaboratoryResults')}</span>
          </Button>
        )}
        <p className="py-8 text-center text-sm text-muted-foreground">{t('noLabResultsFound')}</p>
      </div>
    );
  }

  const value = formatObservationValue(latest) || t('unknown');
  const latestReferenceRange = extractReferenceRange(latest);
  const referenceRange = latestReferenceRange ? `(${t('referenceRange')} ${latestReferenceRange})` : '';
  const latestPerformer = (latest.performer ?? [])
    .map((p) => p.display)
    .filter((d): d is string => Boolean(d))
    .join(', ');

  return (
    <div className="mx-auto w-full max-w-4xl p-5" data-testid="lab-detail">
      {onBack && (
        <Button
          id="back-to-lab-results-button"
          variant="ghost"
          onClick={onBack}
          className="mb-4 flex items-center gap-2"
        >
          <ArrowLeftIcon className="size-4" />
          <span>{backLabel ?? t('backToLaboratoryResults')}</span>
        </Button>
      )}

      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h1 className="text-sm font-medium uppercase">{labName}</h1>
          <p className="text-xs text-muted-foreground">{t('resultCountOnRecord', { count: resultCount })}</p>
        </div>
        {onPromote && <RecordPromotionButton onClick={onPromote} status={promotionStatus} />}
      </div>

      {/* Latest Result Section */}
      {latest && (
        <div className="mb-3">
          <Item size="sm" className="bg-muted/50">
            <ItemContent>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {t('latestResult')}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {t('mostRecentLabel')} {formatDate(latest.effectiveDateTime)}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-semibold">{value}</span>
                {referenceRange && <span className="text-xs text-muted-foreground">{referenceRange}</span>}
                {renderInterpretation(latest)}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div>
                  <span className="text-muted-foreground">{t('status')}</span> {latest.status ?? t('unknown')}
                </div>
                {latest.effectiveDateTime && (
                  <div>
                    <span className="text-muted-foreground">{t('collected')}</span>{' '}
                    {formatDateTime(latest.effectiveDateTime)}
                  </div>
                )}
                {latest.issued && (
                  <div>
                    <span className="text-muted-foreground">{t('reported')}</span> {formatDateTime(latest.issued)}
                  </div>
                )}
                {latestPerformer && (
                  <div>
                    <span className="text-muted-foreground">{t('performer')}</span> {latestPerformer}
                  </div>
                )}
              </div>
              {renderNotes(latest)}
              {renderLabOrderLink(latest)}
            </ItemContent>
          </Item>
        </div>
      )}

      {/* Trend Chart */}
      <LabTrendChart results={sortedResults} />

      {/* Result History Section */}
      {sortedResults.length > 1 && (
        <div>
          <h2 className="mb-2 text-xs font-semibold text-muted-foreground">{t('resultHistory')}</h2>
          <div className="space-y-1">
            {sortedResults.slice(1).map((result, index) => {
              const resultValue = formatObservationValue(result) || t('unknown');
              const resultRefRange = extractReferenceRange(result);
              const resultReferenceRange = resultRefRange ? `${t('reference')} ${resultRefRange}` : '';

              return (
                <Item key={result.id || index} size="sm">
                  <ItemContent>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-xs text-muted-foreground">
                        {result.effectiveDateTime ? formatDate(result.effectiveDateTime) : t('unknown')}
                      </span>
                      <div className="flex items-baseline gap-2">
                        {renderInterpretation(result)}
                        <span className="text-sm font-semibold">{resultValue}</span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {resultReferenceRange && <span>{resultReferenceRange}</span>}
                    </div>
                    {renderNotes(result)}
                    {renderLabOrderLink(result)}
                  </ItemContent>
                </Item>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
